import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

/**
 * Barre d'outils et pied de liste communs à toutes les pages de liste.
 * Ordre fixe, de gauche à droite : segments de périmètre, recherche, filtres,
 * <ToolbarGrow />, bascule de vue, colonnes, action secondaire, action principale.
 * Les filtres qui débordent passent dans <ToolbarFilters />, sous la barre.
 */
export function ListToolbar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex min-h-10 flex-wrap items-center gap-2', className)}>{children}</div>
  );
}

export function ToolbarGrow() {
  return <span className="min-w-2 flex-1" aria-hidden />;
}

export function ToolbarFilters({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex flex-wrap items-center gap-1.5', className)}>{children}</div>;
}

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Compteur affiché après le libellé. */
  count?: number;
}

export function Segments<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: readonly SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  label?: string;
}) {
  return (
    <div role="tablist" aria-label={label} className="inline-flex gap-0.5 rounded-full bg-[#eeebe3] p-[3px]">
      {options.map((option) => {
        const on = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex h-[30px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-[13px] text-[13px] font-medium transition-colors',
              on
                ? 'bg-white text-cime-primary shadow-[0_1px_2px_rgba(0,0,0,0.08)]'
                : 'text-cime-text-muted hover:bg-white/60 hover:text-cime-primary',
            )}
          >
            {option.label}
            {option.count !== undefined ? (
              <b className="font-bold tabular-nums text-cime-gold-text">{option.count}</b>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export function ToolbarSearch({
  value,
  onChange,
  placeholder,
  ariaLabel,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  ariaLabel?: string;
  className?: string;
}) {
  return (
    <label
      className={cn(
        'flex h-9 w-[280px] min-w-[220px] items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 text-[13px] text-neutral-400',
        'hover:border-neutral-300 focus-within:border-cime-primary',
        className,
      )}
    >
      <svg viewBox="0 0 512 512" className="h-3.5 w-3.5 flex-shrink-0" fill="currentColor" aria-hidden>
        {/* Font Awesome Pro 6.5.1 — magnifying-glass regular */}
        <path d="M368 208A160 160 0 1 0 48 208a160 160 0 1 0 320 0zM337.1 371.1C301.7 399.2 256.8 416 208 416C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208c0 48.8-16.8 93.7-44.9 129.1L505 471c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L337.1 371.1z" />
      </svg>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        className="min-w-0 flex-1 border-0 bg-transparent text-cime-text-primary outline-none placeholder:text-neutral-400"
      />
    </label>
  );
}

export interface ViewOption<T extends string> {
  value: T;
  label: string;
  icon: ReactNode;
}

/** Bascule de vue à icônes (Liste / Kanban / Calendrier…). Masquée s'il n'y a qu'une vue. */
export function ViewSwitch<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: readonly ViewOption<T>[];
  value: T;
  onChange: (value: T) => void;
  label: string;
}) {
  if (options.length < 2) return null;
  return (
    <div role="group" aria-label={label} className="inline-flex gap-0.5 rounded-cime bg-[rgba(4,52,41,0.05)] p-[3px]">
      {options.map((option) => {
        const on = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            title={option.label}
            aria-label={option.label}
            aria-pressed={on}
            onClick={() => onChange(option.value)}
            className={cn(
              'grid h-[34px] w-[34px] place-items-center rounded-md transition-colors [&_svg]:h-4 [&_svg]:w-4',
              on
                ? 'bg-white text-cime-primary shadow-[0_1px_2px_rgba(0,0,0,0.08)]'
                : 'text-neutral-400 hover:text-cime-primary',
            )}
          >
            {option.icon}
          </button>
        );
      })}
    </div>
  );
}

/** Pied de liste : état à gauche (« 1–20 sur 23 · tri : échéance ↑ »), pagination à droite. */
export function ListFooter({
  children,
  pagination,
  className,
}: {
  children: ReactNode;
  pagination?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 border-t border-[#e6e2d8] px-4 py-2.5 text-xs text-cime-text-muted',
        className,
      )}
    >
      <span>{children}</span>
      {pagination}
    </div>
  );
}

/** Pagination du pied de liste. `page` commence à 0. */
export function ListPagination({
  page,
  pageCount,
  onChange,
}: {
  page: number;
  pageCount: number;
  onChange: (page: number) => void;
}) {
  if (pageCount <= 1) return null;
  const item =
    'inline-grid h-7 min-w-7 place-items-center rounded-md px-1.5 text-xs text-cime-text-muted transition-colors hover:bg-[#e8f0ec] hover:text-cime-primary disabled:opacity-35 disabled:hover:bg-transparent';
  return (
    <span className="inline-flex items-center gap-1">
      <button type="button" className={item} disabled={page <= 0} onClick={() => onChange(page - 1)} aria-label="Page précédente">
        ‹
      </button>
      {Array.from({ length: pageCount }, (_, i) => i)
        .filter((i) => i === 0 || i === pageCount - 1 || Math.abs(i - page) <= 1)
        .map((i, index, shown) => (
          <span key={i} className="inline-flex items-center gap-1">
            {index > 0 && i - (shown[index - 1] ?? i) > 1 ? <span aria-hidden>…</span> : null}
            <button
              type="button"
              aria-current={i === page ? 'page' : undefined}
              onClick={() => onChange(i)}
              className={cn(item, i === page && 'bg-cime-primary font-semibold text-white hover:bg-cime-primary hover:text-white')}
            >
              {i + 1}
            </button>
          </span>
        ))}
      <button type="button" className={item} disabled={page >= pageCount - 1} onClick={() => onChange(page + 1)} aria-label="Page suivante">
        ›
      </button>
    </span>
  );
}

/** État vide commun : phrase courte centrée, action principale si la création est possible. */
export function ListEmpty({
  icon,
  children,
  action,
}: {
  icon?: ReactNode;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-cime-text-muted">
      {icon ? <span className="text-neutral-300 [&_svg]:h-12 [&_svg]:w-12">{icon}</span> : null}
      <p className="m-0 text-sm">{children}</p>
      {action}
    </div>
  );
}
